/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { useCallback, useState } from "react";
import { useScrollToContainer } from "cms/common/hooks/use-scroll-to-container";
import { ContentZoneContext } from "cms/components/context";
import { useContentZone } from "cms/components/hooks/use-content-zone";
import { ComponentPlacementsDialog } from "cms/components/layout/reuse/placements-dialog/component-placements-dialog";
import { ComponentPositionButtons } from "cms/components/layout/sortable/component-position-buttons";
import { checkDiffs, prepareChangeData, prepareUpdatedObject } from "utils/change-detection";
import { isFalse, isTrue } from "utils/empty-exists";
import { useComposerMode } from "cms/components/hooks/use-composer-mode";
import { useIsLastAddedComponent } from "cms/components/hooks/use-is-last-added-component";
import { ComponentAccordion } from "cms/components/layout/component-accordion";
import { useComponentConfig } from "cms/models/hooks/use-components-config";
import Config from "data/config";
import { Logger } from "cms/utils/log/logger";
import { ComponentErrorFallback } from "./component-error";
import { ErrorBoundary } from "react-error-boundary";

const ContentZoneLayout = (props) => {

    const fieldName = props?.fieldName;
    const component = props?.component;
    const count = props?.count;
    const displayComponent = props?.displayComponent;
    const editComponent = props?.editComponent;
    const position = props?.position;
    const index = props?.index;

    const {
        handleUpdateComponent,
        handleUpdateComponents,
        handleDeleteComponent,
    } = useContentZone();

    const { editMode, contentOnly } = useComposerMode();
    const type = component?.type;
    const label = component?.label;
    const guid = component?.guid;
    const componentConfig = useComponentConfig(type);
    const hideLabel = componentConfig?.hide_label;
    const defaultImpl = componentConfig?.defaultImpl;
    const [componentReuseDialogOpen, setComponentReuseDialogOpen] = useState(false);

    const onCloseFindReuseDialog = () => {
        setComponentReuseDialogOpen(false);
    };

    const { isLast, onScroll } = useIsLastAddedComponent(guid);
    const [expanded, setExpanded] = useState(isLast);
    const ref = useScrollToContainer(guid, isLast, onScroll);

    const componentFromMode = contentOnly ? displayComponent : editComponent;

    const buildInitialState = (component) => {
        let updateComponent = structuredClone(component);
        return {
            ...updateComponent,
        };
    };

    const applyChanges = useCallback((component, updates, source) => {
        if (editMode) {
            const _original = structuredClone(component);
            const _modified = structuredClone(updates);
            const { original, modified } = prepareChangeData(_original, _modified);
            const hasChanged = checkDiffs(original, modified, `content-zone-layout::applyChanges ${source}`);

            if (hasChanged) {
                const updatedComponent = prepareUpdatedObject(original, modified);
                // Logger.debug(`%c content-zone-layout [${source}] -> change detected`, `color: #ff45b5`)
                handleUpdateComponent(fieldName, updatedComponent);
            } else {
                // Logger.debug(`%c content-zone-layout [${source}] -> no change detected during update`, 'color: #ff45b5')
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** @type {ContentZoneContext} */
    let zoneContext = {
        fieldName,
        applyChanges,
        buildInitialState,
        defaultImpl,
        component,
        count,
        editMode,
        expanded,
        handleDeleteComponent,
        handleUpdateComponent,
        handleUpdateComponents,
        position,
        index,
        setComponentReuseDialogOpen,
        hideLabel
    };
    
    return (
        <ContentZoneContext.Provider value={zoneContext}>
            <ErrorBoundary
                FallbackComponent={ComponentErrorFallback}
                onReset={() => {
                    // reset the state of your app so the error doesn't happen again
                    Logger.warn("Component Error occurred and user attempted a reset.");
                    location.reload();
                }}
            >
                {isTrue(contentOnly) && componentFromMode}

                {isFalse(contentOnly) && (
                    <div ref={ref} className={`simplicity-composer-container ${contentOnly ? "preview" : ""}`}>
                        <div className='simplicity-composer'>
                            <ComponentAccordion
                                expanded={expanded}
                                setExpanded={setExpanded}
                            >
                                {componentFromMode}
                            </ComponentAccordion>
                        </div>

                        <div>
                            {editMode && <ComponentPositionButtons />}
                        </div>

                        {/*  Dialog to display component placements */}
                        <ComponentPlacementsDialog
                            fieldName={fieldName}
                            label={label}
                            component={component}
                            open={componentReuseDialogOpen}
                            onClose={onCloseFindReuseDialog}
                        />
                        
                    </div>
                )}

                {Config.Debug.useDebug &&
                    <div style={{ maxWidth: "800px", fontFamily: "monospace" }}><details>
                        <summary> &gt;&gt; Component</summary>
                        <pre style={{ width: "800px", fontSize: "9px", border: "1px solid #999999", maxWidth: "800px", overflowX: "auto" }}>
                            {JSON.stringify(component, null, 4)}
                        </pre>
                    </details></div>
                }
            </ErrorBoundary>
        </ContentZoneContext.Provider>
    );
};

export { ContentZoneLayout };
